import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { GlobalStyle } from "context/theme_provider";

import { AuthMiddleware } from "middleware";
import PageWrapper from "./PageWrapper";

// O visitante do site não precisa baixar editores de contrato, gráficos e mapas.
const named = (loader, name) =>
  lazy(() => loader().then((module) => ({ default: module[name] })));
const Login = named(() => import("pages/login"), "Login");
const Dashboard = named(() => import("pages/dashboard"), "Dashboard");
const Financeiro = named(() => import("pages/financeiro"), "Financeiro");
const Pessoa = named(() => import("pages/pessoa"), "Pessoa");
const Usuario = named(() => import("pages/usuario"), "Usuario");
const ModelosContrato = named(
  () => import("pages/modelos_contrato"),
  "ModelosContrato",
);
const Itens = named(() => import("pages/itens"), "Itens");
const Contrato = named(() => import("pages/contrato"), "Contrato");
const ConvidadosPresentes = lazy(() => import("pages/convidados_presentes"));
const ListaPublicaConvidados = named(
  () => import("pages/convidados_presentes/public/abaConvidados"),
  "ListaPublicaConvidados",
);
const ListaPresentes = lazy(
  () => import("pages/convidados_presentes/public/abaPresentes"),
);
const LojaPresentes = lazy(
  () => import("pages/convidados_presentes/public/loja"),
);
const PortalPublicoConvidadosPresentes = lazy(
  () => import("pages/convidados_presentes/public"),
);
const NotFound = named(() => import("pages/404"), "NotFound");
const MapaFesta = named(() => import("pages/mapa_festa"), "MapaFesta");
const MapaFestaPreview = lazy(() => import("pages/mapa_festa/preview"));
const PublicSite = lazy(() => import("website/PublicSite"));
const WebsiteStudio = lazy(() => import("website/editor/WebsiteStudio"));
const PreviewPage = lazy(() => import("website/PreviewPage"));
const WebsiteScreen = ({ children }) => (
  <Suspense
    fallback={
      <div role="status" style={{ padding: 40 }}>
        Carregando…
      </div>
    }
  >
    {children}
  </Suspense>
);

// Formulários e cores administrativos não devem sobrescrever o tema do cliente.
const RouteStyles = () => {
  const { pathname } = useLocation();
  return pathname === "/" ||
    pathname.replace(/\/$/, "") === "/site/preview" ? null : (
    <GlobalStyle />
  );
};

export const RouteApp = () => (
  <BrowserRouter>
    <RouteStyles />
    <Suspense
      fallback={
        <div role="status" style={{ padding: 40 }}>
          Carregando…
        </div>
      }
    >
      <Routes>
        <Route
          path="/"
          element={
            <WebsiteScreen>
              <PublicSite />
            </WebsiteScreen>
          }
        />
        <Route
          path="/site_configuracao"
          element={
            <AuthMiddleware>
              <WebsiteScreen>
                <WebsiteStudio />
              </WebsiteScreen>
            </AuthMiddleware>
          }
        />
        <Route
          path="/site/preview"
          element={
            <AuthMiddleware>
              <WebsiteScreen>
                <PreviewPage />
              </WebsiteScreen>
            </AuthMiddleware>
          }
        />
        <Route path="/login" element={<Login />} />
        <Route
          path="/dashboard"
          element={
            <AuthMiddleware>
              <PageWrapper title="Dashboard">
                <Dashboard />
              </PageWrapper>
            </AuthMiddleware>
          }
        />
        <Route
          path="/financeiro"
          element={
            <AuthMiddleware>
              <PageWrapper title="Financeiro">
                <Financeiro />
              </PageWrapper>
            </AuthMiddleware>
          }
        />
        <Route
          path="/pessoa"
          element={
            <AuthMiddleware>
              <PageWrapper title="Cadastro de pessoas">
                <Pessoa />
              </PageWrapper>
            </AuthMiddleware>
          }
        />
        <Route
          path="/modelos_contrato"
          element={
            <AuthMiddleware>
              <PageWrapper title="Modelos de contrato">
                <ModelosContrato />
              </PageWrapper>
            </AuthMiddleware>
          }
        />
        <Route
          path="/contratos"
          element={
            <AuthMiddleware>
              <PageWrapper title="Contratos">
                <Contrato />
              </PageWrapper>
            </AuthMiddleware>
          }
        />
        <Route
          path="/convidados-presentes"
          element={
            <AuthMiddleware>
              <PageWrapper title="Convidados & Presentes">
                <ConvidadosPresentes />
              </PageWrapper>
            </AuthMiddleware>
          }
        />
        <Route
          path="/mapa-festa"
          element={
            <AuthMiddleware>
              <PageWrapper title="Mapa da Festa">
                <MapaFesta />
              </PageWrapper>
            </AuthMiddleware>
          }
        />
        <Route
          path="/mapa-festa/:id/preview"
          element={
            <AuthMiddleware>
              <PageWrapper title="Mapa da Festa">
                <MapaFestaPreview />
              </PageWrapper>
            </AuthMiddleware>
          }
        />
        <Route
          path="/usuario"
          element={
            <AuthMiddleware>
              <PageWrapper title="Usuarios do sistema">
                <Usuario />
              </PageWrapper>
            </AuthMiddleware>
          }
        />
        <Route
          path="/itens"
          element={
            <AuthMiddleware>
              <PageWrapper title="Itens de contrato">
                <Itens />
              </PageWrapper>
            </AuthMiddleware>
          }
        />
        <Route path="/convidados/lista" element={<ListaPublicaConvidados />} />
        <Route
          path="/convidados-presentes/:token"
          element={<PortalPublicoConvidadosPresentes />}
        />
        <Route path="/presentes/:token/gestao" element={<ListaPresentes />} />
        <Route path="/presentes/:token" element={<LojaPresentes />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </BrowserRouter>
);
